import React, { useMemo, useState } from 'react';

import { faHistory, faLevelUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { DatePicker, YesNoDialog } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, Checkbox, Dropdown, DropdownContextMenu } from '@ygtechnologies/rubus-ui';
import { orderBy } from 'lodash-es';
import { ResidentialUnitDeadline, ResidentialUnitGroupAndUnitDeadlines, ResidentialUnitGroupDeadline } from '@/types';
import { arrayReplace } from '@ygtechnologies/rubus-frontend-utilities';
import { parseISODate } from '@ygtechnologies/rubus-frontend-utilities';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import theme from './ProjectSettingsComplexDeadlinesTable.module.scss';

interface ProjectSettingsComplexDeadlinesTableProps {
    data: ResidentialUnitGroupAndUnitDeadlines;
    showFloors: boolean;
    showUnits: boolean;
    onChange(newData: ResidentialUnitGroupAndUnitDeadlines): void;
    onRevertUnitGroup(item: ResidentialUnitGroupDeadline): void;
    onRevertUnit(item: ResidentialUnitDeadline): void;
}

const ProjectSettingsComplexDeadlinesTable: React.FC<ProjectSettingsComplexDeadlinesTableProps> = ({
    data,
    showFloors,
    showUnits,
    onChange,
    onRevertUnitGroup,
    onRevertUnit,
}) => {
    const sortedRugData = useMemo(() => {
        return orderBy(data.residentialUnitGroupDeadlines, ['residentialUnitGroupName', 'floor']);
    }, [data.residentialUnitGroupDeadlines]);
    const sortedRuData = useMemo(() => {
        return orderBy(data.residentalUnitDeadlines, ['residentialUnitGroupName', 'floor', 'name']);
    }, [data.residentalUnitDeadlines]);

    const filteredRugData = useMemo(
        () => (showFloors ? sortedRugData : sortedRugData.filter((x) => x.floor === '' || x.edited)),
        [data, showFloors]
    );
    const [saving, setSaving] = useState(false);
    const [revertRow, setRevertRow] = useState<ResidentialUnitDeadline>();
    const [showRevertAllDialog, setShowRevertAllDialog] = useState(false);

    const handleRugChange = (rugRow: ResidentialUnitGroupDeadline, rugChanges: Partial<ResidentialUnitGroupDeadline>) => {
        const newRug = arrayReplace(data.residentialUnitGroupDeadlines!, rugRow, { ...rugRow, ...rugChanges });
        const newData: ResidentialUnitGroupAndUnitDeadlines = {
            ...data,
            residentialUnitGroupDeadlines: newRug,
        };
        triggerHandler(onChange, newData);
    };

    const handleRuChange = (ruRow: ResidentialUnitDeadline, ruChanges: Partial<ResidentialUnitDeadline>) => {
        const newRu = arrayReplace(data.residentalUnitDeadlines!, ruRow, { ...ruRow, ...ruChanges });
        const newData: ResidentialUnitGroupAndUnitDeadlines = {
            ...data,
            residentalUnitDeadlines: newRu,
        };
        triggerHandler(onChange, newData);
    };

    const handleRevertRugClick = (row: ResidentialUnitGroupDeadline) => {
        triggerHandler(onRevertUnitGroup, row);
    };

    const handleRevertAllRu = () => {
        data.residentalUnitDeadlines?.forEach((row) => {
            triggerHandler(onRevertUnit, row);
        });
    };

    const handleRevertDialogClose = () => {
        setRevertRow(undefined);
    };

    const handleRevertDialogOpen = (row: ResidentialUnitDeadline) => {
        setRevertRow(row);
    };

    const handleRevertAllDialogClose = () => {
        setShowRevertAllDialog(false);
    };

    const handleRevertAllDialogOpen = () => {
        setShowRevertAllDialog(true);
    };

    const handleRevertRuClick = (row: ResidentialUnitDeadline) => {
        triggerHandler(onRevertUnit, row);
    };

    const allowEdit = (row: ResidentialUnitGroupDeadline) => {
        row.edited = !row.edited;
        handleRugChange(row, { deadline: row.deadline! });
    };

    const handleEnabled = (row: ResidentialUnitGroupDeadline, isEnabled: boolean) => {
        handleRugChange(row, { isEnabled: isEnabled, deadline: row.deadline!, edited: true });
    };

    return (
        <>
            {showUnits && (
                <>
                    <div className={theme.leftButton}>
                        <Button
                            variant="danger"
                            onClick={() => {
                                handleRevertAllDialogOpen();
                            }}
                            loading={saving}>
                            <Trans>Revert all unit overrides</Trans>
                        </Button>
                    </div>
                    <table className={theme.rubusTable}>
                        <thead>
                            <tr>
                                <th>
                                    <Trans>Unit</Trans>
                                </th>
                                <th>
                                    <Trans>Unit group</Trans>
                                </th>
                                <th>
                                    <Trans>Floor</Trans>
                                </th>
                                <th>
                                    <Trans>Deadline date</Trans>
                                </th>

                                <th>
                                    <Trans>Revert</Trans>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <YesNoDialog
                                show={revertRow !== null && revertRow !== undefined}
                                title={t`Revert` + ': ' + revertRow?.name}
                                body={t`Are you sure you want to revert to the default deadline?`}
                                onAccept={() => handleRevertRuClick(revertRow!)}
                                onClose={handleRevertDialogClose}
                                confirmButtonColor={'primary'}
                                cancelButtonColor={'warning'}
                                confirmButtonText={<Trans>Revert</Trans>}
                                cancelButtonText={<Trans>Cancel</Trans>}
                            />
                            <YesNoDialog
                                title={<Trans>Revert all unit deadlines</Trans>}
                                body={
                                    <Trans>
                                        Are you sure you want to revert all units with overrides in this list to the default
                                        deadlines?
                                    </Trans>
                                }
                                show={showRevertAllDialog}
                                onAccept={() => handleRevertAllRu()}
                                onClose={handleRevertAllDialogClose}
                                confirmButtonColor={'primary'}
                                cancelButtonColor={'warning'}
                                confirmButtonText={<Trans>Revert</Trans>}
                                cancelButtonText={<Trans>Cancel</Trans>}
                            />
                            {sortedRuData.map((row, index) => (
                                <tr key={index}>
                                    <td>{row.name}</td>
                                    <td>
                                        {sortedRugData.find((x) => x.residentialUnitGroupId === row.residentialUnitGroupId)
                                            ?.residentialUnitGroupName ?? <Trans>None</Trans>}
                                    </td>
                                    <td>{row.floor}</td>
                                    <td>
                                        <DatePicker
                                            onChange={(date) => {
                                                if (date) date.setHours(12);
                                                handleRuChange(row, { deadline: date! });
                                            }}
                                            value={parseISODate(row.deadline)}
                                            className="form-control"
                                        />
                                    </td>
                                    <td>
                                        <Button
                                            variant="warning"
                                            onClick={() => {
                                                handleRevertDialogOpen(row);
                                            }}
                                            icon={faHistory}>
                                            <Trans>Revert</Trans>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            )}
            <table className={theme.rubusTable}>
                <thead>
                    <tr>
                        <th>
                            <Trans>Enabled</Trans>
                        </th>
                        <th>
                            <Trans>Unit group</Trans>
                        </th>
                        <th>
                            <Trans>Floor</Trans>
                        </th>
                        <th>
                            <Trans>Deadline date</Trans>
                        </th>
                        <th>
                            <Trans>Edited</Trans>
                        </th>
                        {sortedRugData.some((x) => x.edited) && (
                            <th>
                                <Trans>Menu</Trans>
                            </th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {filteredRugData.map((row, index) => (
                        <tr key={index + row.residentialUnitGroupName}>
                            <td>
                                <Checkbox checked={row.isEnabled} onChange={(e) => handleEnabled(row, e.target.checked)} />
                            </td>
                            <td>
                                <strong>
                                    {row.floor ? (
                                        row.name && showUnits ? (
                                            row.name
                                        ) : (
                                            <FontAwesomeIcon className={theme.indentArrow} icon={faLevelUp} rotation={90} />
                                        )
                                    ) : (
                                        row.residentialUnitGroupName
                                    )}
                                </strong>
                            </td>
                            <td>{row.floor}</td>
                            <td>
                                <form>
                                    <DatePicker
                                        disabled={!row.edited || !row.isEnabled}
                                        onChange={(date) => {
                                            if (date) date.setHours(12);
                                            handleRugChange(row, { deadline: date! });
                                        }}
                                        value={parseISODate(row.deadline)}
                                        className="form-control"
                                    />
                                </form>
                            </td>
                            <td>
                                <Checkbox checked={row.edited} onChange={() => allowEdit(row)}>
                                    {row.edited ? <Trans>Yes</Trans> : <Trans>No</Trans>}
                                </Checkbox>
                            </td>
                            {sortedRugData.some((x) => x.edited) && (
                                <td>
                                    {row.edited && (
                                        <DropdownContextMenu>
                                            <Dropdown.Item
                                                onClick={() => {
                                                    handleRevertRugClick(row);
                                                }}
                                                icon={faHistory}>
                                                {row.floor ? (
                                                    <Trans>Revert to "{row.residentialUnitGroupName}" deadline</Trans>
                                                ) : (
                                                    <Trans>Revert to main deadline</Trans>
                                                )}
                                            </Dropdown.Item>
                                        </DropdownContextMenu>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    );
};

export default ProjectSettingsComplexDeadlinesTable;
